import React, { ComponentProps, FC, useMemo } from 'react';

import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';
import {
  useCurrentProject,
  useCurrentProductId,
  PRODUCT_IDS,
  useCurrentProjectIsTemplate,
  TOOL_IDS,
} from '@adsk/alloy-provider';
import SideNavigation, {
  SideNavigationItem,
} from '@adsk/alloy-side-navigation';

import LocalSideNavigationHeader from './LocalSideNavigationHeader';
import LocalSideNavigationItem from './LocalSideNavigationItem';
import i18n from '../../utils/i18n';
import isExoRoute from '../../utils/isExoRoute';

type LocalSideNavigationProps = ComponentProps<typeof SideNavigation>;

//TODO: PJL-10994: remove this and update usages to the correct tool id once the tool is live and in alloy-provider
export const CONFIGURATION_MEMBERS_TOOL_ID = 'configurationMembers';

const LocalSideNavigation: FC<LocalSideNavigationProps> = (props) => {
  const currentProjectIsTemplate = useCurrentProjectIsTemplate();
  const currentProject = useCurrentProject();
  const currentProductId = useCurrentProductId();

  const finalTools = useMemo(() => {
    const toolsFromProject =
      currentProject?.products?.find((p) => p.key === currentProductId)
        ?.tools || [];

    const demoTool = {
      icon: 'beta',
      key: 'demo-app',
      name: 'demo-app',
      url: `/build/demo-app/projects/${currentProject?.id}`,
    };

    if (
      getEnv() !== ENVS.PRODUCTION &&
      toolsFromProject.length &&
      currentProductId === PRODUCT_IDS.BUILD
    ) {
      return [demoTool, ...toolsFromProject];
    }

    return toolsFromProject;
  }, [currentProject, currentProductId]);

  const templateSetupHeaderItemKey = useMemo(() => {
    if (currentProjectIsTemplate) {
      return finalTools.find((tool) => tool.key === TOOL_IDS.CONFIGURATION)
        ?.key;
    }
    return undefined;
  }, [finalTools, currentProjectIsTemplate]);

  const projectSetupFooterItemKey = useMemo(() => {
    if (currentProjectIsTemplate) {
      const hasConfigurationMembers = finalTools.some(
        (tool) => tool.key === CONFIGURATION_MEMBERS_TOOL_ID,
      );
      if (hasConfigurationMembers) {
        return CONFIGURATION_MEMBERS_TOOL_ID;
      }
      return finalTools.find((tool) => tool.key === TOOL_IDS.CONFIGURATION)
        ?.key;
    }
    return undefined;
  }, [finalTools, currentProjectIsTemplate]);

  return (
    <SideNavigation
      {...props}
      topItems={finalTools as ComponentProps<typeof SideNavigation>['topItems']}
      renderItem={(itemProps) =>
        itemProps.item.url && isExoRoute(itemProps.item.url) ? (
          <LocalSideNavigationItem
            {...itemProps}
            renderHeader={
              itemProps.item.key === templateSetupHeaderItemKey
                ? () => (
                    <LocalSideNavigationHeader
                      title={i18n.t('TEMPLATE_SETUP')}
                    />
                  )
                : undefined
            }
            renderFooter={
              itemProps.item.key === projectSetupFooterItemKey
                ? () => (
                    <LocalSideNavigationHeader
                      title={i18n.t('PROJECT_SETUP')}
                      tooltipTitle={i18n.t('SET_UP_TEMPLATE')}
                      tooltipDescription={i18n.t('SET_UP_TEMPLATE_DESC')}
                      divider={true}
                    />
                  )
                : undefined
            }
          />
        ) : (
          <SideNavigationItem {...itemProps} />
        )
      }
    />
  );
};

LocalSideNavigation.propTypes = {
  ...SideNavigation.propTypes,
};

export default LocalSideNavigation;
